import { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import type { QuizQuestion } from '../../data/quiz';
import { salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { ease } from '../../core/format/motion';
type Props = {
  question: QuizQuestion;
  step: number;
  total: number;
  onAnswer: (label: string) => void;
  onBack: () => void;
};

/**
 * Eén vraag van het huidadvies. Bovenaan "Vorige" en de voortgang (met "2 / 4" erbij: je ziet
 * hoe kort het is), daaronder de vraag groot en de antwoorden als rustige, ruime knoppen.
 * Een vraag schuift zacht opzij bij het wisselen; "minder beweging" = alleen een fade.
 */
export function QuizQuestionStep({
  question,
  step,
  total,
  onAnswer,
  onBack
}: Props) {
  const reduce = useReducedMotion();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // De aangeklikte knop verdwijnt met de vraag: focus naar de nieuwe vraag, zodat toetsenbord en
  // schermlezer niet terug naar het begin springen. Niet bij de eerste: daar zet de lade de focus.
  useEffect(() => {
    if (step > 0) headingRef.current?.focus();
  }, [step]);
  return <div className="flex min-h-full flex-col" data-id="element-8186" data-mp="27crmm8nyruek">
      <div className="flex h-10 items-center gap-4" data-id="element-8187" data-mp="22cvj0aj6i4zj">
        {step > 0 ? <button type="button" onClick={onBack} className="-ml-2 inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full px-2 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8188" data-mp="22ezwnk6vqid0">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8189" data-mp="2q9sty04zq6lo" />
            Vorige
            <span className="sr-only" data-id="element-8190" data-mp="2laybx0eukm2m"> vraag</span>
          </button> : <span className="shrink-0 text-[14px] text-muted" data-id="element-8191" data-mp="3irew6owqbuuh">{total} korte vragen</span>}
        <div className="flex flex-1 items-center gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={step + 1} aria-valuetext={`Vraag ${step + 1} van ${total}`} aria-label="Voortgang huidadvies" data-id="element-8192" data-mp="3e058vuynl3lr">
          {Array.from({
          length: total
        }, (_, i) => <span key={i} aria-hidden="true" className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-line" data-id="element-8193" data-mp="12b8sxuhgx0ym">
              <span className={`absolute inset-0 origin-left rounded-full bg-clay transition-transform duration-300 ease-soft motion-reduce:transition-none ${i <= step ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-8194" data-mp="sf5g9uozpjxf" />
            </span>)}
        </div>
        <span aria-hidden="true" className="shrink-0 text-[13px] tabular-nums text-muted" data-id="element-8195" data-mp="jgk2sdydkv3i">
          {step + 1} / {total}
        </span>
      </div>

      <AnimatePresence mode="wait" initial={false} data-id="element-8196" data-mp="2dt9xuz0hcach">
        <motion.div key={question.id} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -16
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-8197" data-mp="2dqe0bbsjnnok">
          <h3 ref={headingRef} tabIndex={-1} className="mt-8 font-display text-[1.75rem] font-light leading-[1.15] text-ink focus:outline-none" data-id="element-8198" data-mp="2ny02jcn9pske">
            <span className="sr-only" data-id="element-8199" data-mp="2wwbp93l0k67k">
              Vraag {step + 1} van {total}:{' '}
            </span>
            {question.question}
          </h3>
          {question.intro && <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-8200" data-mp="3bnkp05imr7yf">{question.intro}</p>}

          <ul className="mt-7 space-y-2" data-id="element-8201" data-mp="36orcafb63kzb">
            {question.options.map(o => <li key={o.label} data-id="element-8202" data-mp="3klmsqstr0epe">
                <button type="button" onClick={() => onAnswer(o.label)} className="group flex min-h-[60px] w-full items-center justify-between gap-4 rounded-2xl border border-line bg-shell px-5 py-4 text-left transition-[border-color,background-color,transform] duration-150 ease-soft hover:border-ink/40 hover:bg-sand active:scale-[0.99]" data-id="element-8203" data-mp="3gl41azvrpanq">
                  <span data-id="element-8204" data-mp="1smk0au88w50o">
                    <span className="block text-[15px] leading-snug text-ink" data-id="element-8205" data-mp="1slqqtlhwj1eu">{o.label}</span>
                    {o.hint && <span className="mt-1 block text-[13px] leading-snug text-muted" data-id="element-8206" data-mp="1wp6n2gq26zr0">{o.hint}</span>}
                  </span>
                  <ArrowRightIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-ink/30 transition-[color,transform] duration-150 ease-soft group-hover:translate-x-0.5 group-hover:text-ink group-focus-visible:text-ink" strokeWidth={1.5} data-id="element-8207" data-mp="21lnkk1j5eqgr" />
                </button>
              </li>)}
          </ul>
        </motion.div>
      </AnimatePresence>

      <p className="mt-auto pt-10 text-[13px] leading-relaxed text-muted" data-id="element-8208" data-mp="la1if76dar4r">
        Liever direct een mens?{' '}
        <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8209" data-mp="q7t95xva077d">
          App me op {salon.phone}
          <span className="sr-only" data-id="element-8210" data-mp="dr0nvbdjo7tj">{shopLabels.viaWhatsApp}</span>
        </a>
        . Ik antwoord persoonlijk, ook als je nog niets koopt.
      </p>
    </div>;
}